import { useMemo, useState } from "react";
import { Maximize2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { RideDetailsLocation } from "@/types/rideDetails";
import { PIN_COLORS } from "@/utils/rideRouteStops";

export { PIN_COLORS };

export interface RouteMapPoint {
  lat: number;
  lng: number;
  label: string;
  color: string;
}

export function LocationPinIcon({
  color,
  className,
}: {
  color: string;
  className?: string;
}) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 36"
      className={cn("shrink-0", className)}
      aria-hidden
    >
      <path
        fill={color}
        stroke="#ffffff"
        strokeWidth="1.5"
        d="M12 1C6.5 1 2 5.5 2 11c0 8.2 10 23 10 23s10-14.8 10-23C22 5.5 17.5 1 12 1z"
      />
      <circle fill="#ffffff" cx="12" cy="11" r="3.5" />
    </svg>
  );
}

export function RouteStop({
  variant,
  label,
  address,
  action,
}: {
  variant: "start" | "end";
  label: string;
  address: string;
  action?: React.ReactNode;
}) {
  const pinColor = variant === "start" ? PIN_COLORS.start : PIN_COLORS.end;

  return (
    <div className="flex items-start gap-3">
      <LocationPinIcon color={pinColor} className="mt-0.5 h-7 w-5" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {label}
          </p>
          {action}
        </div>
        <p className="text-sm font-medium text-foreground">{address}</p>
      </div>
    </div>
  );
}

function buildMapSrcDoc(points: RouteMapPoint[]): string {
  const data = JSON.stringify(points);
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
  <style>
    html,body,#map{height:100%;margin:0;}
    .route-pin-icon{background:transparent!important;border:none!important;}
  </style>
</head>
<body>
  <div id="map"></div>
  <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
  <script>
    var pts = ${data};
    var map = L.map('map', { attributionControl: false });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(map);
    var latlngs = pts.map(function (p) { return [p.lat, p.lng]; });
    function pinIcon(color) {
      var svg = '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="36" viewBox="0 0 24 36">' +
        '<path fill="' + color + '" stroke="#ffffff" stroke-width="1.5" d="M12 1C6.5 1 2 5.5 2 11c0 8.2 10 23 10 23s10-14.8 10-23C22 5.5 17.5 1 12 1z"/>' +
        '<circle fill="#ffffff" cx="12" cy="11" r="3.5"/>' +
        '</svg>';
      return L.divIcon({
        className: 'route-pin-icon',
        html: svg,
        iconSize: [28, 36],
        iconAnchor: [14, 36],
        popupAnchor: [0, -36]
      });
    }
    pts.forEach(function (p) {
      L.marker([p.lat, p.lng], { icon: pinIcon(p.color) })
        .addTo(map)
        .bindPopup(p.label);
    });
    if (latlngs.length > 1) {
      map.fitBounds(latlngs, { padding: [30, 30] });
    } else if (latlngs.length === 1) {
      map.setView(latlngs[0], 13);
    }
  </script>
</body>
</html>`;
}

function RouteMapFrame({
  points,
  className,
}: {
  points: RouteMapPoint[];
  className?: string;
}) {
  if (points.length === 0) return null;

  return (
    <iframe
      title="Route map"
      srcDoc={buildMapSrcDoc(points)}
      loading="lazy"
      className={cn("w-full border border-border", className)}
    />
  );
}

function locationToMapPoint(
  location: RideDetailsLocation,
  label: string,
  color: string
): RouteMapPoint | null {
  if (typeof location.lat !== "number" || typeof location.lng !== "number") {
    return null;
  }
  return { lat: location.lat, lng: location.lng, label, color };
}

function resolveMapPoints(
  pickup?: RideDetailsLocation,
  drop?: RideDetailsLocation,
  mapPoints?: RouteMapPoint[]
): RouteMapPoint[] {
  if (mapPoints && mapPoints.length > 0) return mapPoints;

  return [
    pickup
      ? locationToMapPoint(
          pickup,
          pickup.label ?? "Start Location",
          PIN_COLORS.start
        )
      : null,
    drop
      ? locationToMapPoint(drop, drop.label ?? "End Location", PIN_COLORS.end)
      : null,
  ].filter((p): p is RouteMapPoint => p != null);
}

export function RouteMap({
  pickup,
  drop,
  mapPoints,
  className,
  expandable = false,
}: {
  pickup?: RideDetailsLocation;
  drop?: RideDetailsLocation;
  mapPoints?: RouteMapPoint[];
  className?: string;
  expandable?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  const points = useMemo(
    () => resolveMapPoints(pickup, drop, mapPoints),
    [pickup, drop, mapPoints]
  );

  if (points.length === 0) return null;

  return (
    <div className={cn("relative", className)}>
      <RouteMapFrame points={points} className="h-44 rounded-xl" />

      {expandable && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-lg bg-background/90 text-foreground shadow-sm ring-1 ring-border transition-colors hover:bg-background"
          aria-label="Expand map"
        >
          <Maximize2 className="h-4 w-4" />
        </button>
      )}

      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent className="max-w-4xl gap-3 p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>Route map</DialogTitle>
          </DialogHeader>
          <RouteMapFrame points={points} className="h-[70vh] rounded-xl" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
